import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { TNPSC_UNIT1_QUESTIONS } from "../data/tnpscUnit1Questions";

// ─────────────────────────────────────────────────────────────────────────────
// TNPSC Code 554 — Unit I: Programming in C, Python and Object Oriented
// Programming (C++). Content is drafted from the official syllabus topic
// list; each concept has an explanation plus a runnable-style example/script.
// NOT YET human-reviewed by a subject-matter expert — treat as a first draft.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["C Programming", "Python", "OOP (C++)"];

const SECTION_COLORS = {
  "C Programming": "#2563eb",
  "Python":        "#16a34a",
  "OOP (C++)":     "#7c3aed",
};

const CONCEPTS = [
  // ── C Programming ──────────────────────────────────────────────────────
  { section: "C Programming", title: "Data types and type conversion",
    explanation: "C's basic types are char, int, float, double (plus signed/unsigned and short/long qualifiers). sizeof() reports each type's size in bytes, which is implementation-dependent (commonly int=4 bytes, char=1 byte). Implicit conversion (type promotion) happens automatically in mixed-type expressions; explicit conversion is a programmer-written cast like (float)x.",
    example: `#include <stdio.h>
int main() {
    printf("%d\\n", 7 / 2);        // 3  — integer division truncates
    printf("%f\\n", 7 / 2.0);      // 3.500000 — forces float division
    printf("%f\\n", (float)7 / 2); // 3.500000 — explicit cast
    return 0;
}` },
  { section: "C Programming", title: "Input and output operations",
    explanation: "C uses printf() for output and scanf() for input. Both rely on format specifiers such as %d (int), %f (float), %lf (double), %c (char), and %s (string). scanf() requires variable addresses using &, except for strings which already represent a pointer.",
    example: `#include <stdio.h>
int main() {
    int a; float b; char c;
    scanf("%d %f %c", &a, &b, &c);
    printf("Values: %d %.2f %c\\n", a, b, c);
    return 0;
}` },
  { section: "C Programming", title: "Initialization vs. declaration",
    explanation: "A declaration introduces a variable and its type (int x;). Initialization assigns a first value at the moment of declaration (int x = 10;). Local variables without initialization contain garbage values, while global (and static) variables default to zero.",
    example: `int x;      // declaration only — garbage value if local
int y = 5;  // declaration + initialization` },
  { section: "C Programming", title: "Decision making and branching",
    explanation: "if/else chooses between two branches; switch compares one variable against multiple constant case labels and, WITHOUT a break statement, execution 'falls through' into the next case rather than stopping. The ternary operator (condition ? a : b) is a compact single-expression if/else.",
    example: `switch (2) {
    case 1: printf("a");
    case 2: printf("b");   // no break — falls through
    case 3: printf("c"); break;
}
// prints "bc", not just "b"` },
  { section: "C Programming", title: "Looping statements",
    explanation: "for is best when the number of iterations is known in advance. while checks its condition BEFORE the first iteration (may run zero times). do-while checks its condition AFTER the first iteration, so the body always runs at least once. break exits the loop entirely; continue skips to the next iteration.",
    example: `do {
    printf("hi");
} while (0);
// prints "hi" once, even though the condition is false —
// a while(0){...} loop with the same body prints nothing` },
  { section: "C Programming", title: "One and two dimensional arrays",
    explanation: "A 1D array int a[5] holds 5 contiguous elements, indexed 0 to 4. A 2D array int a[3][4] is stored in ROW-MAJOR order — element a[i][j] is at offset (i*4 + j). C does NOT check array bounds at runtime, so a[5] on a 5-element array is undefined behavior, not a caught error.",
    example: `int a[3][4];
// a[1][2] is the (1*4 + 2) = 6th element (0-indexed)
// in the array's underlying linear memory layout` },
  { section: "C Programming", title: "Matrix operations",
    explanation: "Matrices are implemented using 2D arrays. Addition is element-wise. Multiplication uses triple nested loops: C[i][j] = Σ(A[i][k] * B[k][j]). TNPSC frequently tests indexing and loop structure.",
    example: `// Matrix addition
for (i = 0; i < m; i++)
    for (j = 0; j < n; j++)
        C[i][j] = A[i][j] + B[i][j];

// Matrix multiplication
for (i = 0; i < m; i++)
    for (j = 0; j < p; j++) {
        C[i][j] = 0;
        for (k = 0; k < n; k++)
            C[i][j] += A[i][k] * B[k][j];
    }` },
  { section: "C Programming", title: "Strings and string operations",
    explanation: "A C string is a char array terminated by a NUL byte ('\\0'). strlen() returns the string's LENGTH (characters before the '\\0'), while sizeof() on a char array returns its total ALLOCATED size — these are frequently confused. Key string.h functions: strcpy, strcat, strcmp (returns 0 if equal), strlen.",
    example: `char s[20] = "hello";
printf("%lu\\n", strlen(s)); // 5
printf("%lu\\n", sizeof(s)); // 20 — the array's declared size` },
  { section: "C Programming", title: "String arrays",
    explanation: "An array of strings is typically either a 2D char array (char names[5][20], fixed row width) or an array of char pointers (char *names[5]), where each pointer can point to a differently-sized string — more memory-efficient, but each pointer must be initialized to valid memory.",
    example: `char *fruits[] = { "apple", "fig", "kiwi" };
// each element is a POINTER to a string literal of different
// length — a 2D array would waste space padding "fig"/"kiwi"` },
  { section: "C Programming", title: "Sorting and searching",
    explanation: "Sorting algorithms like bubble sort and selection sort run in O(n²). Searching includes linear search (O(n)) and binary search (O(log n)), which requires a sorted array. TNPSC often tests algorithm behavior and complexity.",
    example: `int binary(int a[], int n, int key) {
    int l = 0, r = n - 1;
    while (l <= r) {
        int mid = (l + r) / 2;
        if (a[mid] == key) return mid;
        if (key < a[mid]) r = mid - 1;
        else l = mid + 1;
    }
    return -1;
}` },
  { section: "C Programming", title: "Functions: definition, declaration, pass by value/reference",
    explanation: "A function DECLARATION (prototype) just states the signature; the DEFINITION provides the actual body. C is strictly PASS BY VALUE — a called function gets a COPY of each argument, so it cannot modify the caller's original variable directly. 'Pass by reference' in C is simulated by passing a POINTER to the variable.",
    example: `void inc(int x)  { x++; }        // pass by value — no effect on caller
void incRef(int *x) { (*x)++; }  // pass by "reference" via a pointer

int main() {
    int n = 5;
    inc(n);     // n is still 5
    incRef(&n); // n becomes 6
}` },
  { section: "C Programming", title: "Recursion",
    explanation: "A recursive function calls itself, and MUST have a base case that stops the recursion — without one, it recurses infinitely until the call stack overflows. Each recursive call gets its own copy of local variables/parameters on the call stack.",
    example: `int fact(int n) {
    if (n <= 1) return 1;   // base case
    return n * fact(n - 1); // recursive case
}` },
  { section: "C Programming", title: "Pointers: definition, initialization, arithmetic",
    explanation: "A pointer variable stores a memory ADDRESS, declared as type *p. An uninitialized pointer holds a garbage address; p = NULL explicitly marks 'points to nothing'. Pointer arithmetic is scaled by the pointed-to type's size — p+1 moves forward by sizeof(*p) bytes, not 1 byte.",
    example: `int x = 10;
int *p = &x;      // p holds the address of x
printf("%d", *p);  // 10 — dereference to get the value
// if p == 1000 and sizeof(int) == 4, then p + 1 == 1004` },
  { section: "C Programming", title: "Pointers and arrays",
    explanation: "An array's name, when used in most expressions, 'decays' into a pointer to its first element — so a[i] and *(a+i) are equivalent. However, an array is NOT itself a pointer variable: sizeof(array) gives the whole array's size, while sizeof(pointer) always gives just the pointer's own size.",
    example: `int a[10];
int *p = a;
printf("%lu\\n", sizeof(a)); // 40 (10 ints × 4 bytes)
printf("%lu\\n", sizeof(p)); // 8  (just a pointer, 64-bit system)` },
  { section: "C Programming", title: "Structure definition and declaration",
    explanation: "A structure groups related variables under one name. The definition creates a new type, and declarations create variables of that type. Structures allow mixing of different data types.",
    example: `struct Student {
    int roll;
    float marks;
};
struct Student s1 = { 101, 89.5 };` },
  { section: "C Programming", title: "Structure within a structure (nested structures)",
    explanation: "A structure can contain another structure as a member. Access uses chained dot operators. Nested structures help model real-world hierarchical data.",
    example: `struct Date { int d, m, y; };
struct Student {
    int roll;
    struct Date dob;
};
struct Student s = { 101, { 12, 5, 2004 } };
printf("%d-%d-%d", s.dob.d, s.dob.m, s.dob.y);` },
  { section: "C Programming", title: "Unions",
    explanation: "A union groups members, but ALL members SHARE the same memory location — a union's size equals its LARGEST member's size, and writing one member overwrites any value stored in another. Only one member holds a valid value at a time.",
    example: `union Data { int i; float f; };
union Data d;
d.i = 10;
printf("%d\\n", d.i);   // 10
d.f = 3.14;
printf("%f\\n", d.f);   // 3.14 — overwrote d.i's memory` },
  { section: "C Programming", title: "Storage classes",
    explanation: "auto (default for local variables) exists only within its block. static, on a local variable, RETAINS its value between function calls; on a global/function, it restricts visibility to the same file. extern declares a variable/function defined elsewhere. register suggests keeping a variable in a CPU register.",
    example: `void f() {
    static int count = 0;
    count++;
    printf("%d ", count);
}
// f(); f(); f();  →  prints "1 2 3" (static persists)
// a plain "int count = 0;" would print "1 1 1"` },
  { section: "C Programming", title: "Preprocessor directives",
    explanation: "#define creates a macro — pure TEXT SUBSTITUTION before compilation, with no type checking (unlike a const variable). #include pulls in another file's contents.",
    example: `#define SQUARE(x) x*x
// SQUARE(1+2) expands to 1+2*1+2 = 5, NOT 9!
// fix:
#define SQUARE(x) ((x)*(x))
// SQUARE(1+2) now correctly expands to 9` },
  { section: "C Programming", title: "File handling — reading and writing",
    explanation: "File operations use FILE*. fopen() opens a file, fprintf()/fscanf() write/read formatted data, fgets()/fputs() handle strings, and fclose() closes the file. Modes include \"r\", \"w\", \"a\", \"r+\", \"w+\".",
    example: `FILE *fp = fopen("data.txt", "w");
fprintf(fp, "Hello TNPSC");
fclose(fp);

fp = fopen("data.txt", "r");
char s[20];
fgets(s, 20, fp);
printf("%s", s);
fclose(fp);` },

  // ── Python ──────────────────────────────────────────────────────────────
  { section: "Python", title: "Interpreter, interactive mode, and dynamic typing",
    explanation: "Python is typically an INTERPRETED language (via CPython's bytecode compilation + virtual machine) rather than compiled directly to native code like C. Python is DYNAMICALLY TYPED — a variable's type is determined at runtime from the value assigned, and can change.",
    example: `x = 5
x = "hello"   # perfectly legal in Python
# in C:  int x = 5; x = "hello";  →  compile error` },
  { section: "Python", title: "Data types, statements, expressions, and Booleans",
    explanation: "Python's core built-in types include int, float, str, bool, list, tuple, dict, set. A STATEMENT performs an action; an EXPRESSION evaluates to a value. Boolean operators are the WORDS and, or, not (not symbols like C's &&, ||, !), and they short-circuit.",
    example: `print(0 or "hello")   # "hello" — Python's or/and return
                       # the actual operand VALUE, not just True/False` },
  { section: "Python", title: "Lists, tuples, and dictionaries",
    explanation: "A list [1,2,3] is MUTABLE and ordered. A tuple (1,2,3) is IMMUTABLE and ordered — often used for fixed collections or dict keys (tuples of immutable elements are hashable, lists are not). A dict {'a':1} stores key-value pairs and preserves insertion order (Python 3.7+).",
    example: `my_list = [1, 2, 3]
my_list[0] = 99          # works fine

my_tuple = (1, 2, 3)
my_tuple[0] = 99          # TypeError: 'tuple' object
                           # does not support item assignment` },
  { section: "Python", title: "Functions and file reading/writing",
    explanation: "Python functions are defined with def and can have default argument values. A classic pitfall is using a MUTABLE default (like a list), since it's evaluated ONCE at definition time and shared across calls. *args collects extra positional args into a tuple; **kwargs collects extra keyword args into a dict.",
    example: `def f(x, lst=[]):
    lst.append(x)
    return lst

print(f(1))  # [1]
print(f(2))  # [1, 2]  — NOT [2]! same default list is reused

with open("data.txt") as f:   # auto-closes even on exception
    text = f.read()` },

  // ── OOP (C++) ───────────────────────────────────────────────────────────
  { section: "OOP (C++)", title: "Data abstraction and encapsulation",
    explanation: "Encapsulation bundles data and the methods that operate on it into a single unit (a class), restricting direct outside access (via private/protected). Data Abstraction shows only the ESSENTIAL interface while hiding complexity — encapsulation is the MECHANISM, abstraction is the DESIGN GOAL.",
    example: `class Account {
private:
    double balance;   // hidden implementation
public:
    void deposit(double amt) { balance += amt; } // exposed interface
};` },
  { section: "OOP (C++)", title: "Class, object, and constructors",
    explanation: "A class is a user-defined blueprint; an object is an INSTANCE of that class. A constructor is a special member function with the SAME NAME as the class and NO return type, called automatically on object creation. Types: default, parameterized, and copy constructor.",
    example: `class Point {
public:
    int x, y;
    Point() : x(0), y(0) {}               // default
    Point(int a, int b) : x(a), y(b) {}   // parameterized
    Point(const Point &p) : x(p.x), y(p.y) {} // copy
};` },
  { section: "OOP (C++)", title: "Static members and constant members",
    explanation: "A static member belongs to the CLASS itself, not to any one object — SHARED across every object of that class. A static member function can only access other static members directly. A const member function promises NOT to modify the object's state.",
    example: `class Counter {
    static int count;
public:
    Counter() { count++; }   // every object shares "count"
};
int Counter::count = 0;` },
  { section: "OOP (C++)", title: "The 'this' pointer",
    explanation: "Inside any non-static member function, 'this' is an implicit pointer to the object the function was called ON — used to resolve a naming clash between a parameter and a member with the same name, and to return *this for method chaining.",
    example: `class Point {
    int x;
public:
    void setX(int x) { this->x = x; } // this->x is the member,
                                       // x is the parameter
};` },
  { section: "OOP (C++)", title: "Polymorphism: overloading vs. virtual functions",
    explanation: "COMPILE-TIME polymorphism is resolved before running: function/operator overloading. RUN-TIME polymorphism is resolved during execution via VIRTUAL FUNCTIONS — a base class pointer calling a virtual function invokes the DERIVED class's overridden version, decided by the object's real type.",
    example: `class Base    { public: virtual void show() { cout << "Base"; } };
class Derived : public Base { public: void show() override { cout << "Derived"; } };

Base *b = new Derived();
b->show();  // prints "Derived" — resolved at run time` },
  { section: "OOP (C++)", title: "Abstract classes and pure virtual functions",
    explanation: "A pure virtual function is declared with '= 0' and has no implementation in that class. A class with at least one pure virtual function is an ABSTRACT CLASS, which CANNOT be instantiated directly — a derived class MUST override every pure virtual function to be instantiable.",
    example: `class Shape {
public:
    virtual void draw() = 0;   // pure virtual
};
// Shape s;  →  compile error: cannot instantiate abstract class

class Circle : public Shape {
public:
    void draw() override { /* ... */ }  // now instantiable
};` },
  { section: "OOP (C++)", title: "Inheritance types",
    explanation: "Single: one derived from one base. Multiple: one derived from two or more bases. Multilevel: a chain (C derives from B, which derives from A). Hierarchical: multiple derived classes from ONE common base. Hybrid: a combination of the above, which can cause the 'diamond problem'.",
    example: `class C : public A, public B {};       // multiple inheritance
class B : public A {};
class C2 : public B {};                 // multilevel: A → B → C2` },
  { section: "OOP (C++)", title: "Exception handling",
    explanation: "C++ exception handling uses try (where an exception MIGHT occur), throw (raises an exception), and catch (handles a specific type). If no matching catch handles it, the program terminates. catch(...) catches ANY exception type.",
    example: `try {
    throw 5;
} catch (int e) {
    cout << "caught " << e;   // prints "caught 5"
} catch (...) {
    cout << "unknown";
}` },
  { section: "OOP (C++)", title: "Templates and generic programming",
    explanation: "Templates let you write ONE function or class that works for MULTIPLE data types — the compiler generates a type-specific version when the template is actually used. A function template parameterizes one function; a class template parameterizes an entire class.",
    example: `template <typename T>
T maxVal(T a, T b) { return (a > b) ? a : b; }

maxVal(3, 5);      // works for ints
maxVal(2.5, 1.1);  // works for doubles — same definition` },
  { section: "OOP (C++)", title: "STL: containers, iterators, function adaptors, allocators",
    explanation: "STL CONTAINERS store collections — vector (dynamic array, fast random access), list (linked list, fast insert/delete), map (sorted key-value pairs), set (sorted unique elements). ITERATORS traverse ANY container uniformly. Function adaptors/functors wrap functions as objects. Allocators abstract memory management.",
    example: `std::vector<int> v = {3, 1, 2};
std::vector<int>::iterator it = v.begin();
while (it != v.end()) { cout << *it; ++it; }
// the same iterator syntax also walks a std::list` },
  { section: "OOP (C++)", title: "Dynamic memory allocation and nested classes",
    explanation: "In C++, 'new' allocates memory AND calls the constructor; 'delete' calls the destructor AND frees the memory — this is why new/delete is preferred over C's malloc()/free(), which only allocate raw memory with no constructor/destructor calls. A nested class is defined inside another class's scope.",
    example: `MyClass *obj = new MyClass();  // allocates + constructs
delete obj;                    // destructs + frees

// malloc(sizeof(MyClass)) would allocate raw memory only —
// the constructor would never run` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function TnpscUnit1Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  const filtered = useMemo(() => (
    activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection)
  ), [activeSection]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? TNPSC_UNIT1_QUESTIONS : TNPSC_UNIT1_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div style={S.page}>
      <Link to="/tnpsc" style={S.back}>← Back to TNPSC 554 Menu</Link>
      {" · "}
      <Link to="/tnpsc-unit2" style={S.back}>Unit II →</Link>
      {" · "}
      <Link to="/tnpsc-unit3" style={S.back}>Unit III →</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🎓 TNPSC 554 — Unit I</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Programming in C, Python and Object Oriented Programming</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          20 questions in the exam · {CONCEPTS.length} concepts explained · {TNPSC_UNIT1_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({TNPSC_UNIT1_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a href={`#concept-${i}`} style={{ color:"#e2e8f0", textDecoration:"none" }}>{c.title}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / SCRIPT</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
      ))}
      </>
      )}
    </div>
  );
}
